import { useState, useEffect, useCallback, useContext } from 'react';
import { constants } from '@constants/Constants';
import { useLayoutNavigation } from './layout-navigation-hook';
import useCustomMediaQuery from '@utils/useCustomMediaQuery';
import store from '../store/store';
import { FilterContext } from '@components/FilterBar/FilterProvider';
import {
  getStartOfMonth,
  getEndOfMonth,
  getCurrentDayOfMonth,
} from 'components/FilterBar/helper';
import { useGoLiveSource } from './useGoLiveSource';

/**
 * Enhanced custom hook that encapsulates ALL filter-related logic
 * This separates ALL business logic from UI components
 */
export const useFilterLogic = ({ hideR360PP = false }) => {
  const filterContext = useContext(FilterContext);
  const { selectCustomStyles, handleProductPageRequestData } =
    useLayoutNavigation();

  const isMFE = store.getState().app.isMFE;
  const isMobile = useCustomMediaQuery('(max-width: 600px)');
  const isTablet = useCustomMediaQuery(
    '(min-width: 601px) and (max-width: 1024px)'
  );

  // Local state for dropdown options
  const [programOptions, setProgramOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  const [sectorOptions, setSectorOptions] = useState([]);
  const [marketOptions, setMarketOptions] = useState([]);
  const [plantOptions, setPlantOptions] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [displayModel, setDisplayModel] = useState(false);

  // Date-related state
  const [goLiveMonth, setGoLiveMonth] = useState(new Date().getMonth());
  const [goLiveYear, setGoLiveYear] = useState(new Date().getFullYear());
  const [selectedDateForMonthPicker, setSelectedDateForMonthPicker] = useState(
    new Date()
  );

  // Use CMS-driven goLiveSource resolution (only active when CMS config exists for current product)
  // Legacy hardcoded useEffects below handle products not yet onboarded to DatoCMS
  const { cmsGoLiveSourceConfig } = useGoLiveSource({
    setGoLiveMonth,
    setGoLiveYear,
  });

  // Filter programs based on hideR360PP
  // Using queueMicrotask to defer context state updates and avoid
  // "Cannot update a component while rendering a different component" warning
  useEffect(() => {
    if (
      Array.isArray(filterContext.regionValues) &&
      filterContext.regionValues.length > 0
    ) {
      const filteredRegions = hideR360PP
        ? filterContext.regionValues.filter(
            (program) => ![2, 3, 4, 5, 6, 7].includes(program?.programId)
          )
        : filterContext.regionValues;

      if (filteredRegions && filteredRegions.length > 0) {
        setProgramOptions(filteredRegions);
        // Defer context update to avoid updating parent during child render
        queueMicrotask(() => {
          filterContext.setSelectedProgram(filteredRegions[0]);
        });
      }
    }
  }, [
    filterContext.regionValues,
    hideR360PP,
    filterContext.setSelectedProgram,
  ]);

  // Filter products based on selected program
  useEffect(() => {
    if (filterContext.selectedProgram) {
      const filterCondition = hideR360PP
        ? (product) =>
            product?.id !== constants.products.R360 &&
            product?.id !== constants.products.ShipmentVisibility
        : () => true;

      const products = filterContext.productValues.filter(
        (product) =>
          product.programId === filterContext.selectedProgram.programId &&
          filterCondition(product)
      );

      if (products && products.length > 0) {
        if (
          !filterContext.selectedProduct ||
          filterContext.selectedProduct.programId !==
            filterContext.selectedProgram.programId
        ) {
          // Defer context update to avoid updating parent during child render
          queueMicrotask(() => {
            filterContext.setSelectedProduct(products[0]);
          });
        }
        setProductOptions(products);
      } else {
        queueMicrotask(() => {
          filterContext.setSelectedProduct(null);
        });
      }
    }
  }, [
    filterContext.selectedProgram,
    hideR360PP,
    filterContext.setSelectedProduct,
    filterContext.productValues,
  ]);

  // Update sectors and models based on selected product
  useEffect(() => {
    if (filterContext.selectedProduct) {
      const sectors = filterContext.sectorValues.filter(
        ({ productId }) =>
          productId === filterContext.selectedProduct.productId &&
          productId !== constants.products.PromoAI &&
          productId !== constants.products.PricingAI
      );
      const sortedSectors = sectors.sort((a, b) =>
        a.sectorName.localeCompare(b.sectorName)
      );

      // Defer context updates to avoid updating parent during child render
      queueMicrotask(() => {
        filterContext.setSelectedSector(sortedSectors?.[0] || {});
      });
      setSectorOptions(sortedSectors || []);

      const isGuidedSelling =
        filterContext.selectedProduct.productId ===
        constants.products.GuidedSelling;
      setDisplayModel(isGuidedSelling);

      const filteredModels = filterContext.modelValues.filter((model) =>
        hideR360PP
          ? model?.productId === filterContext.selectedProduct.productId &&
            model?.id !== 2 &&
            model?.id !== 7
          : model?.productId === filterContext.selectedProduct.productId
      );
      setModelOptions(filteredModels);
      queueMicrotask(() => {
        filterContext.setSelectedModel(filteredModels?.[0] || null);
      });
    }
  }, [
    filterContext.selectedProduct,
    filterContext.sectorValues,
    filterContext.setSelectedSector,
    hideR360PP,
    filterContext.modelValues,
    filterContext.setSelectedModel,
  ]);

  // Update sectors based on model for Guided Selling
  useEffect(() => {
    if (
      displayModel &&
      filterContext.selectedModel &&
      Object.keys(filterContext.selectedModel).length !== 0
    ) {
      const sectors = filterContext.sectorValues.filter(
        (sector) =>
          sector?.productId === filterContext.selectedProduct?.productId &&
          sector?.modelId === filterContext.selectedModel?.id
      );
      const sortedSectors = sectors.sort((a, b) =>
        a.sectorName.localeCompare(b.sectorName)
      );
      queueMicrotask(() => {
        filterContext.setSelectedSector(sortedSectors?.[0] || {});
      });
      setSectorOptions(sortedSectors || []);
    }
  }, [
    filterContext.selectedModel,
    displayModel,
    filterContext.setSelectedSector,
    filterContext.sectorValues,
  ]);

  // NEW: Handle application names filtering based on selected market
  useEffect(() => {
    if (
      filterContext.allApplicationNames !== undefined &&
      filterContext.selectedMarket !== null
    ) {
      const platformNames = filterContext.allApplicationNames
        .filter(
          (appName) =>
            appName.marketId === filterContext.selectedMarket.marketId &&
            appName.productId === filterContext.selectedMarket.productId
        )
        .map((appName) => appName?.platform);
      queueMicrotask(() => {
        filterContext.setSelectedApplicaitonNames(platformNames);
      });
    }
  }, [
    filterContext.selectedMarket,
    filterContext.allApplicationNames,
    filterContext.setSelectedApplicaitonNames,
  ]);

  // NEW: Enhanced product change handler with API logging and go-live date setting
  useEffect(() => {
    if (!filterContext.selectedProduct?.productId) {
      return;
    }

    const productId = filterContext.selectedProduct.productId;

    // Product name mapping for API logging
    const getProductName = (id) => {
      const productMap = {
        [constants.products.PepsiConnect]: 'PepsiConnect',
        [constants.products.R360]: 'R360',
        [constants.products.GuidedSelling]: 'GuidedSelling',
        [constants.products.E2EWaste]: 'E2EWaste',
        [constants.products.MLT]: 'MLT',
        [constants.products.ShipmentVisibility]: 'ShipmentVisibility',
        [constants.products.Automation]: 'Automation',
        [constants.products.PromoAI]: 'PromoAI',
        [constants.products.Canvas]: 'Canvas',
        [constants.products.PricingAI]: 'PricingAI',
        [constants.products.DTC]: 'DTC',
        [constants.products.ManufacturingControlTower]:
          'ManufacturingControlTower',
        [constants.products.GPG]: 'GPG',
        [constants.products.AIQ]: 'AIQ',
        [constants.products.DTVPortal]: 'DTVPortal',
      };
      return productMap[id] || '';
    };

    const productName = getProductName(productId);

    // API call for product logging
    handleProductPageRequestData({
      variables: {
        productId,
        productName,
      },
    });

    // Skip legacy goLiveDate logic if CMS config is available for this product
    if (cmsGoLiveSourceConfig) {
      return;
    }

    // Set go-live date based on product
    const productsWithDirectGoLive = [
      constants.products.Canvas,
      constants.products.PromoAI,
      constants.products.PricingAI,
      constants.products.APP,
      constants.products.o9Solutions,
      constants.products.Automation,
      constants.products.GPG,
      constants.products.IPC,
      constants.products.Compass,
      constants.products.DTVPortal,
      constants.products.ProcurementHub,
      constants.products.FLAg,
    ];

    if (productsWithDirectGoLive.includes(productId)) {
      const dateForGoLive = new Date(filterContext.selectedProduct.goLiveDate);
      filterContext.setGoLiveDate(dateForGoLive);
      setGoLiveMonth(dateForGoLive.getMonth());
      setGoLiveYear(dateForGoLive.getFullYear());
    }
  }, [
    filterContext.selectedProduct?.productId,
    filterContext.selectedProduct?.goLiveDate,
    handleProductPageRequestData,
    cmsGoLiveSourceConfig,
  ]);

  // NEW: Set go-live date based on market selection
  useEffect(() => {
    // Skip if CMS config is available for this product
    if (cmsGoLiveSourceConfig) {
      return;
    }

    const productsWithMarketBasedGoLive = [
      constants.products.PepsiConnect,
      constants.products.R360,
      constants.products.E2EWaste,
      constants.products.MLT,
      constants.products.PepsiTastyRewards,
      constants.products.DTC,
      constants.products.Kazandirio,
      constants.products.ShouldCostModelling,
      constants.products.MenuPro,
      constants.products.AIQ,
      constants.products.POE,
      constants.products.MCVO,
    ];

    const isGuidedSellingWithSpecificModels =
      filterContext.selectedProduct?.productId ===
        constants.products.GuidedSelling &&
      (filterContext.selectedModel?.id === constants.modals.suggestedOrder ||
        filterContext.selectedModel?.id === constants.modals.nbA ||
        filterContext.selectedModel?.id === constants.modals.smartTargets);

    if (
      productsWithMarketBasedGoLive.includes(
        filterContext.selectedProduct?.productId
      ) ||
      isGuidedSellingWithSpecificModels
    ) {
      let dateForGoLive = new Date(filterContext.selectedMarket?.goLiveDate);

      if (dateForGoLive.toString() === 'Invalid Date') {
        dateForGoLive = new Date();
      }

      filterContext.setGoLiveDate(dateForGoLive);
      setGoLiveMonth(dateForGoLive.getMonth());
      setGoLiveYear(dateForGoLive.getFullYear());
    }
  }, [
    filterContext.selectedMarket,
    filterContext.selectedProduct,
    filterContext.selectedModel,
    cmsGoLiveSourceConfig,
  ]);

  // NEW: Set go-live date based on model selection for Guided Selling
  useEffect(() => {
    // Skip if CMS config is available for this product
    if (cmsGoLiveSourceConfig) {
      return;
    }

    if (
      filterContext.selectedProduct?.productId ===
        constants.products.GuidedSelling &&
      (filterContext.selectedModel?.id === constants.modals.shareWise ||
        filterContext.selectedModel?.id === constants.modals.productPairing)
    ) {
      const dateForGSModel = new Date(filterContext.selectedModel.goLiveDate);
      filterContext.setGoLiveDate(dateForGSModel);
      setGoLiveMonth(dateForGSModel.getMonth());
      setGoLiveYear(dateForGSModel.getFullYear());
    }
  }, [
    filterContext.selectedModel,
    filterContext.selectedProduct,
    cmsGoLiveSourceConfig,
  ]);

  // NEW: Set go-live date based on sector selection
  useEffect(() => {
    // Skip if CMS config is available for this product
    if (cmsGoLiveSourceConfig) {
      return;
    }

    const productsWithSectorBasedGoLive = [
      constants.products.ShipmentVisibility,
      constants.products.Archlet,
      constants.products.TPO,
      constants.products.ProfitToServe,
      constants.products.AIQ,
    ];

    if (
      productsWithSectorBasedGoLive.includes(
        filterContext.selectedProduct?.productId
      )
    ) {
      const dateForGoLive = new Date(filterContext.selectedSector?.goLiveDate);
      filterContext.setGoLiveDate(dateForGoLive);
      setGoLiveMonth(dateForGoLive.getMonth());
      setGoLiveYear(dateForGoLive.getFullYear());
    }
  }, [
    filterContext.selectedSector,
    filterContext.selectedProduct,
    cmsGoLiveSourceConfig,
  ]);

  // NEW: Set go-live date based on plant/site selection
  useEffect(() => {
    // Skip if CMS config is available for this product
    if (cmsGoLiveSourceConfig) {
      return;
    }

    const productsWithPlantBasedGoLive = [
      constants.products.ManufacturingControlTower,
      constants.products.MesLite,
    ];

    if (
      productsWithPlantBasedGoLive.includes(
        filterContext.selectedProduct?.productId
      )
    ) {
      const dateForMCT = new Date(filterContext.selectedPlant?.goLiveDate);
      filterContext.setGoLiveDate(dateForMCT);
      setGoLiveMonth(dateForMCT.getMonth());
      setGoLiveYear(dateForMCT.getFullYear());
    }
  }, [
    filterContext.selectedPlant,
    filterContext.selectedProduct,
    cmsGoLiveSourceConfig,
  ]);

  // Update markets based on sector
  const updateMarketsBasedOnSector = useCallback(
    (selectedSector) => {
      if (selectedSector) {
        const markets = filterContext.marketValues
          .filter(
            (market) =>
              market.productId === selectedSector.productId &&
              market.sectorId === selectedSector.sectorId &&
              market.modelId === selectedSector.modelId
          )
          .sort((a, b) => a.label.localeCompare(b.label));

        if (markets && markets.length > 0) {
          filterContext.setSelectedMarket(markets[0]);
          setMarketOptions(markets);
        } else {
          filterContext.setSelectedMarket({});
          setMarketOptions([]);
        }
      }
    },
    [filterContext.marketValues, filterContext.setSelectedMarket]
  );

  // Update plants based on sector
  const updatePlantsBasedOnSector = useCallback(
    (selectedSector) => {
      if (selectedSector) {
        const plants = filterContext.plantValues.filter(
          (plant) =>
            plant.productId === selectedSector.productId &&
            plant.sectorId === selectedSector.sectorId
        );
        if (plants && plants.length > 0) {
          filterContext.setSelectedPlant(plants[0]);
          setPlantOptions(plants);
        } else {
          filterContext.setSelectedPlant({});
          setPlantOptions([]);
        }
      }
    },
    [filterContext.plantValues, filterContext.setSelectedPlant]
  );

  // Update customers based on market
  const updateCustomersBasedOnMarket = useCallback(
    (selectedMarket) => {
      if (selectedMarket) {
        const filteredMarkets = filterContext.marketValues.filter(
          (market) =>
            market.productId === selectedMarket.productId &&
            market.sectorId === selectedMarket.sectorId &&
            market.marketId === selectedMarket.marketId
        );
        if (filteredMarkets && filteredMarkets.length > 0) {
          const customers = filteredMarkets[0].customers;
          const transformedCustomers = customers.map((customer) => ({
            id: customer?.customerId,
            label: customer?.customerName,
            value: customer?.customerName,
            goLiveDate: customer?.goLiveDate,
          }));
          const defaultCustomer = transformedCustomers.find((customer) =>
            customer.label.startsWith('WALMART')
          );
          filterContext.setSelectedCustomer(
            defaultCustomer ||
              (transformedCustomers.length > 0 ? transformedCustomers[0] : {})
          );
          setCustomerOptions(transformedCustomers);
        } else {
          filterContext.setSelectedCustomer({});
          setCustomerOptions([]);
        }
      }
    },
    [filterContext.marketValues, filterContext.setSelectedCustomer]
  );

  const updateSectorOption = useCallback(
    (option) => {
      filterContext.setSelectedSector(option);
      updateMarketsBasedOnSector(option);
      updatePlantsBasedOnSector(option);
    },
    [
      updateMarketsBasedOnSector,
      updatePlantsBasedOnSector,
      filterContext.setSelectedSector,
    ]
  );

  // NEW: Date handling functions moved from FilterBar
  const setYTDDate = useCallback(() => {
    const currentDate = new Date();
    const currentYear = filterContext.selectedYear;
    const firstDayOfYear = new Date(currentYear, 0, 1);
    const firstDayOfYearUTC = new Date(
      firstDayOfYear.getTime() - firstDayOfYear.getTimezoneOffset() * 60000
    );
    filterContext.setSelectedStartDate(firstDayOfYear);
    filterContext.setStartUTCDate(firstDayOfYearUTC.toISOString());

    const selectedDate =
      filterContext.selectedYear === currentDate.getFullYear()
        ? currentDate
        : new Date(currentYear, 12, 0);
    filterContext.setSelectedDate(selectedDate);

    const monthDate =
      filterContext.selectedYear === new Date().getFullYear()
        ? getCurrentDayOfMonth()
        : getEndOfMonth(new Date(filterContext.selectedYear, 12, 0));
    const endOfMonth =
      filterContext.selectedYear === new Date().getFullYear()
        ? getEndOfMonth(new Date())
        : getEndOfMonth(new Date(filterContext.selectedYear, 12, 0));
    const selectedEndDate =
      filterContext.selectedProduct?.productId ===
      constants?.products?.ShipmentVisibility
        ? monthDate
        : endOfMonth;
    filterContext.setSelectedEndDate(selectedEndDate);
    filterContext.setEndUTCDate(selectedEndDate.toISOString());
  }, [
    filterContext.selectedYear,
    filterContext.selectedProduct,
    getCurrentDayOfMonth,
    getEndOfMonth,
    filterContext.setSelectedStartDate,
    filterContext.setStartUTCDate,
    filterContext.setSelectedDate,
    filterContext.setSelectedEndDate,
    filterContext.setEndUTCDate,
  ]);

  const setMonthDate = useCallback(() => {
    filterContext.setSelectedStartDate(
      getStartOfMonth(filterContext.selectedDate, true)
    );
    filterContext.setStartUTCDate(
      getStartOfMonth(filterContext.selectedDate).toISOString()
    );
    const currentDate =
      filterContext.selectedYear === new Date().getFullYear()
        ? getCurrentDayOfMonth()
        : getEndOfMonth(new Date(filterContext.selectedYear, 12, 0));
    const endOfMonth =
      filterContext.selectedYear === new Date().getFullYear()
        ? getEndOfMonth(new Date())
        : getEndOfMonth(new Date(filterContext.selectedYear, 12, 0));
    const selectedEndDate =
      filterContext.selectedProduct?.productId ===
      constants?.products?.ShipmentVisibility
        ? currentDate
        : endOfMonth;
    filterContext.setSelectedEndDate(selectedEndDate);
    filterContext.setEndUTCDate(selectedEndDate.toISOString());
  }, [
    filterContext.selectedDate,
    filterContext.selectedYear,
    filterContext.selectedProduct,
    getStartOfMonth,
    getCurrentDayOfMonth,
    getEndOfMonth,
    filterContext.setSelectedStartDate,
    filterContext.setStartUTCDate,
    filterContext.setSelectedEndDate,
    filterContext.setEndUTCDate,
  ]);

  const handleCheckboxChange = useCallback(
    (event) => {
      filterContext.setYTDCheckbox(event.target.checked);
      if (event?.target?.checked) {
        setYTDDate();
      } else {
        setMonthDate();
      }
    },
    [filterContext.setYTDCheckbox, setYTDDate, setMonthDate]
  );

  // Effect hooks for updating dependent dropdowns
  useEffect(() => {
    updateMarketsBasedOnSector(filterContext.selectedSector);
  }, [filterContext.selectedSector, updateMarketsBasedOnSector]);

  useEffect(() => {
    updatePlantsBasedOnSector(filterContext.selectedSector);
  }, [filterContext.selectedSector, updatePlantsBasedOnSector]);

  useEffect(() => {
    if (
      filterContext.selectedProduct?.productId === constants?.products?.R360
    ) {
      updateCustomersBasedOnMarket(filterContext.selectedMarket);
    }
  }, [
    filterContext.selectedMarket,
    updateCustomersBasedOnMarket,
    filterContext.selectedProduct,
  ]);

  return {
    // Context values
    ...filterContext,

    // Local state
    programOptions,
    productOptions,
    modelOptions,
    sectorOptions,
    marketOptions,
    plantOptions,
    customerOptions,
    displayModel,

    // Date-related state
    goLiveMonth,
    setGoLiveMonth,
    goLiveYear,
    setGoLiveYear,
    selectedDateForMonthPicker,
    setSelectedDateForMonthPicker,

    // Helper functions
    updateSectorOption,
    setYTDDate,
    setMonthDate,
    handleCheckboxChange,

    // Responsive flags
    isMobile,
    isTablet,
    isMFE,

    // Styles and utilities
    selectCustomStyles,
    getStartOfMonth,
    getEndOfMonth,
    getCurrentDayOfMonth,
  };
};
